import { observable, action, computed, runInAction } from 'mobx';
import io from 'socket.io-client';
import { notification, message } from 'antd';
import { PositionStore } from './PositionStore';
import { IndicatorStore } from './IndicatorStore';
import { BalanceStore } from './BalanceStore';
import { MarketStore } from './MarketStore';
import { ActionStore } from './ActionsStore';
import { TriggerStore } from './TriggerStore';
import { TickerStore } from './TickerStore';
import axios from 'axios/index';

export class BotStore {
    @observable currentBot = null; //BotConfig id
    @observable currentConfiguration = null;
    @observable connected = false;
    @observable subscription = null;

    @observable poolBindings = {};
    @observable bots = {};

    @observable initialDataLoaded = false;
    @observable.struct positions = [];
    @observable usedStrats = {};

    @observable dailyPerformance = [];
    @observable coinPerformance = [];
    @observable portfolioHistory = [];
    @observable autoDCALogs = [];
    @observable autoRTLogs = [];
    @observable logs = [];
    @observable initialLogs = [];
    @observable signals = [];

    constructor({ rootStore }) {
        this.rootStore = rootStore;

        this.positionStore = new PositionStore(this);
        this.indicatorStore = new IndicatorStore(this);
        this.balanceStore = new BalanceStore(this);
        this.marketStore = new MarketStore(this);
        this.actionStore = new ActionStore(this);
        this.triggerStore = new TriggerStore(this);
        this.tickersStore = new TickerStore(this);
    }

    @action connectToStream(id) {
        const botConfig = this.bots[id];

        runInAction(() => {
            this.currentBot = botConfig.id;
            this.currentConfiguration = botConfig;
        });

        this.socket = io(
            this.pool.env === 'local'
                ? `//${this.pool.location}`
                : `https://${this.currentConfiguration.exchange}.apextrader.io/`,
            {
                path: this.pool.env === 'local' ? '' : `/${this.pool.id}/socket.io/`,
                query: `token=${this.rootStore.userStore.idToken}`
            }
        );

        this.socket.on('connect', () => {
            runInAction(() => {
                this.connected = true;
            });
        });

        this.socket.on('disconnect', () => {
            runInAction(() => {
                this.connected = false;
            });
        });

        this.socket.on('positions', ({ data }) => {
            runInAction(() => {
                this.positions = data;
            });
        });

        this.socket.on('logEntry', ({ data }) => {
            runInAction(() => {
                const exists = this.logs.filter(log => log.time === data.time);
                if (exists.length) return;
                this.logs.unshift(data);
                if (this.logs.length === 1000) this.logs.pop();
            });
        });

        this.socket.on('notification', ({ data }) => {
            notification[data.type || 'info']({
                message: data.title,
                description: data.message
            });

            if (data.sound && this.rootStore.userStore.currentUser.sound) {
                const audio = new Audio(`/${data.sound}.mp3`);
                audio.play().catch(e => console.log(e));
            }
        });
        this.positionStore.getRecentlyClosed();
        this.indicatorStore.listen();
        this.balanceStore.listen();
        this.tickersStore.listen();
        this.getLogs();
    }

    @action async getSubscription(poolId) {
        try {
            const subscription = await this.rootStore.billingStore.getSubscription(poolId);
            runInAction(() => {
                this.subscription = subscription.data;
            });
        } catch (e) {
            console.log(e);
        }
    }

    async getKline(marketId, tf) {
        const data = await this.proxy(`v1/kline/${marketId}/${tf}`);
        return data;
    }

    calculateUsedStrats() {
        let usedStrats = {};
        Object.values(this.bots).forEach(bot => {
            if (bot.art_strategy_id)
                usedStrats[bot.art_strategy_id] = { id: bot.id, name: bot.pool_id };
            if (bot.adca_strategy_id)
                usedStrats[bot.adca_strategy_id] = { id: bot.id, name: bot.pool_id };
            if (bot.strategy_id) usedStrats[bot.strategy_id] = { id: bot.id, name: bot.pool_id };

            //default trigger
            if (bot.default_trigger && bot.default_trigger.actions.length) {
                bot.default_trigger.actions.forEach(action => {
                    if (action.id === 'switch_strategy' && action.strategy_id) {
                        usedStrats[action.strategy_id] = { id: bot.id, name: bot.pool_id };
                    }
                });
            }

            if (bot.triggers && bot.triggers.length > 0) {
                bot.triggers.forEach(trigger => {
                    if (!trigger.type || trigger.type !== 'strategy') return;

                    usedStrats[trigger.strategy_id] = { id: bot.id, name: bot.pool_id };

                    trigger.buy_actions.forEach(action => {
                        if (action.id === 'switch_strategy' && action.strategy_id) {
                            usedStrats[action.strategy_id] = { id: bot.id, name: bot.pool_id };
                        }
                    });
                    trigger.sell_actions.forEach(action => {
                        if (action.id === 'switch_strategy' && action.strategy_id) {
                            usedStrats[action.strategy_id] = { id: bot.id, name: bot.pool_id };
                        }
                    });
                });
            }
        });

        this.usedStrats = usedStrats;
    }

    @action closeStream() {
        this.socket.close();
        this.positions = [];
        this.indicatorStore.close();
        this.triggerStore.close();
        this.autoSignalWatcher && this.autoSignalWatcher();
        this.logs = [];
        this.initialLogs = [];
    }

    @action fetchUserPool() {
        const currentUser = this.rootStore.fbAuth.currentUser;
        if (!currentUser) throw new Error('not permitted');

        this.poolWatcher = this.rootStore.db
            .collection(`pool`)
            .where('uid', '==', currentUser.uid)
            .onSnapshot(snapshot => {
                const next = {};
                snapshot.forEach(pool => {
                    next[pool.id] = { id: pool.id, ...pool.data() };
                });

                runInAction(() => {
                    this.poolBindings = next;
                    this.isLoading = false;
                });
            });
    }

    /**
     * Open a realtime stream for users bots and poolBindings records
     * @returns {Promise}
     */
    @action getUserBots() {
        const currentUser = this.rootStore.fbAuth.currentUser;
        if (!currentUser) throw new Error('not permitted');

        this.fetchUserPool();

        this.configWatcher = this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/`)
            .where('deleted', '==', false)
            .onSnapshot(snapshot => {
                let recordSet = {};
                snapshot.forEach(recordSnapshot => {
                    recordSet[recordSnapshot.id] = {
                        id: recordSnapshot.id,
                        ...recordSnapshot.data()
                    };
                });
                runInAction(() => {
                    this.bots = recordSet;
                    this.initialDataLoaded = true;
                });
                this.calculateUsedStrats();
            });
    }

    get connectionURI() {
        return this.pool.env === 'local'
            ? `//${this.pool.location}`
            : `https://${this.currentConfiguration.exchange}.apextrader.io/${this.pool.id}`;
    }
    /**
     * Make a request to the bots status server
     * @param endpoint
     */
    async proxy(endpoint) {
        const res = await axios.get(`${this.connectionURI}/${endpoint}/`);
        return res.data;
    }

    /**
     * Get poolBindings record for the current bot
     * @returns {*}
     */
    @computed get pool() {
        const values = Object.values(this.poolBindings);

        for (let i = 0; i < values.length; i++) {
            if (values[i].bot_config === this.currentBot) return values[i];
        }

        return null;
    }

    @action close() {
        this.configWatcher && this.configWatcher();
        this.poolWatcher && this.poolWatcher();
        this.dailyPerformanceWatcher && this.dailyPerformanceWatcher();
        this.coinPerformanceWatcher && this.coinPerformanceWatcher();
        this.autoDCALogWatcher && this.autoDCALogWatcher();
        this.autoRTLogWatcher && this.autoRTLogWatcher();
        this.positionStore.stopRecentlyClosed();
        //this.triggerStore.close();
    }

    async generateSignalEmailAddress(botConfig) {
        await axios.get(`${this.rootStore.env.signalsApi}generate_token?id=${botConfig}`, {
            headers: {
                Authorization: `Bearer ${this.rootStore.userStore.idToken}`
            }
        });
    }

    async generateSignalSecret(botConfig) {
        await axios.get(`${this.rootStore.env.signalsApi}generate_secret?id=${botConfig}`, {
            headers: {
                Authorization: `Bearer ${this.rootStore.userStore.idToken}`
            }
        });
    }

    /**
     * Update multiple configuration keys
     * @param botId
     * @param values
     */
    @action updateConfig(values) {
        if (!values.api_key) delete values.api_key;
        if (!values.api_secret) delete values.api_secret;
        if (!values.password) delete values.password;

        const currentUser = this.rootStore.fbAuth.currentUser;
        if (!currentUser) return Promise.reject();

        this.currentConfiguration = { ...this.currentConfiguration, ...values };
        message.success('Settings saved');
        return this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/`)
            .doc(this.currentBot)
            .set(values, { merge: true });
    }

    /**
     * Update a single configuration key
     * @param botId
     * @param key
     * @param value
     * @returns {*}
     */
    @action updateConfigValue(key, value) {
        const currentUser = this.rootStore.fbAuth.currentUser;
        if (!currentUser) return Promise.reject();
        this.currentConfiguration[key] = value;
        return this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/`)
            .doc(this.currentBot)
            .update({ [key]: value });
    }

    @action async getAutoDCALogs() {
        const currentUser = this.rootStore.fbAuth.currentUser;

        this.autoDCALogWatcher = this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/${this.currentBot}/dca_logs`)
            .limit(30)
            .orderBy('created_at', 'desc')
            .onSnapshot(snapshot => {
                let recordSet = [];
                snapshot.forEach(recordSnapshot => {
                    recordSet.push(recordSnapshot.data());
                });
                runInAction(() => {
                    this.autoDCALogs = recordSet;
                });
            });
    }

    @action async getAutoRTLogs() {
        const currentUser = this.rootStore.fbAuth.currentUser;

        this.autoRTLogWatcher = this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/${this.currentBot}/reverse_logs`)
            .limit(30)
            .orderBy('created_at', 'desc')
            .onSnapshot(snapshot => {
                let recordSet = [];
                snapshot.forEach(recordSnapshot => {
                    recordSet.push(recordSnapshot.data());
                });
                runInAction(() => {
                    this.autoRTLogs = recordSet;
                });
            });
    }

    @action async getSignals() {
        const currentUser = this.rootStore.fbAuth.currentUser;

        this.autoSignalWatcher = this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/${this.currentBot}/signals`)
            .limit(10)
            .orderBy('created_at', 'desc')
            .onSnapshot(snapshot => {
                let recordSet = [];
                snapshot.forEach(recordSnapshot => {
                    recordSet.push(recordSnapshot.data());
                });
                runInAction(() => {
                    this.signals = recordSet;
                });
            });
    }

    @action getDailyPerformance() {
        const currentUser = this.rootStore.fbAuth.currentUser;
        if (!currentUser) return Promise.reject();

        this.dailyPerformanceWatcher = this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/${this.currentBot}/daily_performance`)
            .limit(30)
            .orderBy('day', 'desc')
            .onSnapshot(snapshot => {
                let recordSet = [];
                snapshot.forEach(recordSnapshot => {
                    recordSet.push({ id: recordSnapshot.id, ...recordSnapshot.data() });
                });
                runInAction(() => {
                    this.dailyPerformance = recordSet;
                });
            });
    }

    @action getCoinPerformance() {
        const currentUser = this.rootStore.fbAuth.currentUser;
        if (!currentUser) return Promise.reject();
        let period = new Date();
        period.setDate(period.getDate() - 30);
        this.coinPerformanceWatcher = this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/${this.currentBot}/coin_performance`)
            .limit(30)
            .orderBy('day', 'desc')
            .onSnapshot(snapshot => {
                let recordSet = [];
                snapshot.forEach(recordSnapshot => {
                    recordSet.push(recordSnapshot.data());
                });
                runInAction(() => {
                    this.coinPerformance = recordSet;
                });
            });
    }

    @action async getPortfolioHistory() {
        const currentUser = this.rootStore.fbAuth.currentUser;

        if (!currentUser) return Promise.reject();
        let period = new Date();
        period.setDate(period.getDate() - 30);
        const snapshot = await this.rootStore.db
            .collection(`users/${currentUser.uid}/botConfigs/${this.currentBot}/portfolio_snaps`)
            .limit(24 * 30)
            .where('created_at', '>=', period.getTime())
            .orderBy('created_at', 'asc')
            .get();

        runInAction(() => {
            let recordSet = [];
            snapshot.forEach(recordSnapshot => {
                recordSet.push({ id: recordSnapshot.id, ...recordSnapshot.data() });
            });
            this.portfolioHistory = recordSet;
        });
    }

    @action async clearPortfolioHistory() {
        const currentUser = this.rootStore.fbAuth.currentUser;

        for (const entry of this.portfolioHistory) {
            this.rootStore.db
                .collection(
                    `users/${currentUser.uid}/botConfigs/${this.currentBot}/portfolio_snaps/`
                )
                .doc(entry.id)
                .delete();
        }
        runInAction(() => (this.portfolioHistory = []));
    }

    @action async clearCoinHistory() {
        const currentUser = this.rootStore.fbAuth.currentUser;
        for (const entry of this.dailyPerformance) {
            await this.rootStore.db
                .collection(
                    `users/${currentUser.uid}/botConfigs/${this.currentBot}/coin_performance/`
                )
                .doc(entry.id)
                .delete();
        }
        runInAction(() => (this.coinPerformance = []));
    }

    @action async clearDailyHistory() {
        const currentUser = this.rootStore.fbAuth.currentUser;
        await this.clearCoinHistory();
        for (const entry of this.dailyPerformance) {
            await this.rootStore.db
                .collection(
                    `users/${currentUser.uid}/botConfigs/${this.currentBot}/daily_performance/`
                )
                .doc(entry.id)
                .delete();
        }
        await this.updateConfigValue('statistics_start_from', Date.now());
        runInAction(() => (this.dailyPerformance = []));
    }

    @action async getLogs() {
        const res = await axios.get(`${this.rootStore.env.botApi}${this.currentBot}/logs`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.initialLogs = res.data.logs.split('\n').reverse();
        });
    }

    async reboot() {
        await axios.get(`${this.rootStore.env.botApi}${this.currentBot}/reboot`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        notification.success({
            message: 'Bot rebooting',
            description: 'This may take a few minutes.'
        });
    }

    async removeBot() {
        await axios.get(`${this.rootStore.env.botApi}${this.currentBot}/delete`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });

        notification.success({
            message: 'Bot removed',
            description: 'This may take a few minutes.'
        });
    }

    async createBot(config) {
        return await axios.post(`${this.rootStore.env.botApi}create`, config, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
    }

    async createOdysseyBot(config) {
        return await axios.post(`${this.rootStore.env.botApi}odyssey/create`, config, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
    }

    async createMakoBot(config) {
        return await axios.post(`${this.rootStore.env.botApi}mako/create`, config, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
    }
}



// WEBPACK FOOTER //
// ./src/stores/BotStore.js